import { useCallback, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';

import { fireOperationalEvent } from '../analytics';

import { useTeamsPermissionsStore } from './teams-permission-store';
import { type TeamsPermissionsService } from './teams-permission-store/types';

export const useTeamsPermissionsService: TeamsPermissionsService = (
	scope,
	{ enabled, attributes } = { enabled: false, attributes: {} },
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// backwards compatibility allow orgId to be passed as a string
	const { orgId, cloudId } =
		typeof scope === 'string' || !scope ? { orgId: scope, cloudId: undefined } : scope;

	const [state, { getPermissions }] = useTeamsPermissionsStore();
	const { fireEvent } = useAnalyticsEventsNext();

	const fetchData = useCallback(async () => {
		getPermissions(
			{ orgId, cloudId, enabled },
			{
				onFetch: () => {
					if (fg('ptc-enable-teams-common-analytics-refactor')) {
						// @ts-expect-error - hyphenated event names are not supported in the analytics spec
						fireEvent('operational.use-team-permissions.fetched', {
							userId: attributes?.userId,
							orgId,
							cloudId,
						});
					} else {
						fireOperationalEvent(createAnalyticsEvent, {
							action: 'fetched',
							actionSubject: 'use-team-permissions',
							attributes: {
								userId: attributes?.userId,
								orgId,
								cloudId,
							},
						});
					}
				},
			},
		);
	}, [
		orgId,
		cloudId,
		enabled,
		getPermissions,
		createAnalyticsEvent,
		attributes?.userId,
		fireEvent,
	]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return {
		loading: state.isLoading,
		data: state.permissionsResponse,
		error: state.error,
		...state,
	};
};
